// Shared constants for profile options used in onboarding and personal profile
import { useTranslation } from 'react-i18next';

// English values for backend storage (always stored in English)
export const EXPERIENCE_LEVELS_EN = {
    NEW_TO_TAROT: "New to tarot",
    SOME_EXPERIENCE: "Some experience",
    EXPERIENCED_READER: "Experienced reader"
} as const;

export const READING_FREQUENCIES_EN = {
    DAILY: "Daily",
    WEEKLY: "Weekly",
    OCCASIONALLY: "Occasionally"
} as const;

export const DECK_PREFERENCES_EN = {
    RIDER_WAITE: "Rider-Waite",
    CELESTIAL_MINIMALIST: "Celestial Minimalist",
    THOTH: "Thoth"
} as const;

export const USER_GOALS_EN = {
    LOVE_RELATIONSHIPS: "Love / Relationships",
    CAREER_FINANCES: "Career / Finances",
    PERSONAL_GROWTH: "Personal Growth",
    HEALTH_WELLBEING: "Health / Well-being",
    CURIOSITY_EXPLORATION: "Curiosity / Exploration"
} as const;

export const AI_TONE_PREFERENCES_EN = {
    DIRECT_PRACTICAL: "Direct and practical",
    GENTLE_INTUITIVE: "Gentle and intuitive",
    DEEP_SYMBOLIC: "Deep and symbolic"
} as const;

export const LANGUAGES_EN = {
    ENGLISH: "English",
    PORTUGUESE: "Portuguese",
    SPANISH: "Spanish",
    FRENCH: "French",
    GERMAN: "German"
} as const;

// Hook to get translated profile options for display
export const useProfileOptions = () => {
    const { t } = useTranslation();

    return {
        EXPERIENCE_LEVELS: {
            NEW_TO_TAROT: t('profileOptions.experienceLevels.newToTarot'),
            SOME_EXPERIENCE: t('profileOptions.experienceLevels.someExperience'),
            EXPERIENCED_READER: t('profileOptions.experienceLevels.experiencedReader')
        },
        READING_FREQUENCIES: {
            DAILY: t('profileOptions.readingFrequencies.daily'),
            WEEKLY: t('profileOptions.readingFrequencies.weekly'),
            OCCASIONALLY: t('profileOptions.readingFrequencies.occasionally')
        },
        DECK_PREFERENCES: {
            RIDER_WAITE: t('profileOptions.deckPreferences.riderWaite'),
            CELESTIAL_MINIMALIST: t('profileOptions.deckPreferences.celestialMinimalist'),
            THOTH: t('profileOptions.deckPreferences.thoth')
        },
        USER_GOALS: {
            LOVE_RELATIONSHIPS: t('profileOptions.userGoals.loveRelationships'),
            CAREER_FINANCES: t('profileOptions.userGoals.careerFinances'),
            PERSONAL_GROWTH: t('profileOptions.userGoals.personalGrowth'),
            HEALTH_WELLBEING: t('profileOptions.userGoals.healthWellbeing'),
            CURIOSITY_EXPLORATION: t('profileOptions.userGoals.curiosityExploration')
        },
        AI_TONE_PREFERENCES: {
            DIRECT_PRACTICAL: t('profileOptions.aiTonePreferences.directPractical'),
            GENTLE_INTUITIVE: t('profileOptions.aiTonePreferences.gentleIntuitive'),
            DEEP_SYMBOLIC: t('profileOptions.aiTonePreferences.deepSymbolic')
        },
        LANGUAGES: {
            ENGLISH: t('profileOptions.languages.english'),
            PORTUGUESE: t('profileOptions.languages.portuguese'),
            SPANISH: t('profileOptions.languages.spanish'),
            FRENCH: t('profileOptions.languages.french'),
            GERMAN: t('profileOptions.languages.german')
        }
    };
};

// Helper function to get English value for a translated value
export const getEnglishValue = (translatedValue: string, category: 'experience' | 'frequency' | 'deck' | 'goal' | 'tone' | 'language') => {
    const englishMappings = {
        experience: EXPERIENCE_LEVELS_EN,
        frequency: READING_FREQUENCIES_EN,
        deck: DECK_PREFERENCES_EN,
        goal: USER_GOALS_EN,
        tone: AI_TONE_PREFERENCES_EN,
        language: LANGUAGES_EN
    };

    const mapping = englishMappings[category];
    const entries = Object.entries(mapping);

    // Find the English key that corresponds to the translated value
    for (const [key, englishValue] of entries) {
        if (englishValue === translatedValue) {
            return englishValue;
        }
    }

    // If not found, return the original value (fallback)
    return translatedValue;
};

// Helper function to get translated value for an English value
export const getTranslatedValue = (englishValue: string, category: 'experience' | 'frequency' | 'deck' | 'goal' | 'tone' | 'language') => {
    const { t } = useTranslation();

    const translationKeys: Record<string, Record<string, string>> = {
        experience: {
            'New to tarot': 'profileOptions.experienceLevels.newToTarot',
            'Some experience': 'profileOptions.experienceLevels.someExperience',
            'Experienced reader': 'profileOptions.experienceLevels.experiencedReader'
        },
        frequency: {
            'Daily': 'profileOptions.readingFrequencies.daily',
            'Weekly': 'profileOptions.readingFrequencies.weekly',
            'Occasionally': 'profileOptions.readingFrequencies.occasionally'
        },
        deck: {
            'Rider-Waite': 'profileOptions.deckPreferences.riderWaite',
            'Celestial Minimalist': 'profileOptions.deckPreferences.celestialMinimalist',
            'Thoth': 'profileOptions.deckPreferences.thoth'
        },
        goal: {
            'Love / Relationships': 'profileOptions.userGoals.loveRelationships',
            'Career / Finances': 'profileOptions.userGoals.careerFinances',
            'Personal Growth': 'profileOptions.userGoals.personalGrowth',
            'Health / Well-being': 'profileOptions.userGoals.healthWellbeing',
            'Curiosity / Exploration': 'profileOptions.userGoals.curiosityExploration'
        },
        tone: {
            'Direct and practical': 'profileOptions.aiTonePreferences.directPractical',
            'Gentle and intuitive': 'profileOptions.aiTonePreferences.gentleIntuitive',
            'Deep and symbolic': 'profileOptions.aiTonePreferences.deepSymbolic'
        },
        language: {
            'English': 'profileOptions.languages.english',
            'Portuguese': 'profileOptions.languages.portuguese',
            'Spanish': 'profileOptions.languages.spanish',
            'French': 'profileOptions.languages.french',
            'German': 'profileOptions.languages.german'
        }
    };

    const keys = translationKeys[category];
    const translationKey = keys[englishValue];

    return translationKey ? t(translationKey) : englishValue;
};

// Legacy constants for backward compatibility (deprecated - use English constants instead)
export const EXPERIENCE_LEVELS = EXPERIENCE_LEVELS_EN;
export const READING_FREQUENCIES = READING_FREQUENCIES_EN;
export const DECK_PREFERENCES = DECK_PREFERENCES_EN;
export const USER_GOALS = USER_GOALS_EN;
export const AI_TONE_PREFERENCES = AI_TONE_PREFERENCES_EN;
export const LANGUAGES = LANGUAGES_EN;

// Helper functions to get all values as arrays (legacy - use English constants instead)
export const getExperienceLevels = () => Object.values(EXPERIENCE_LEVELS_EN);
export const getReadingFrequencies = () => Object.values(READING_FREQUENCIES_EN);
export const getDeckPreferences = () => Object.values(DECK_PREFERENCES_EN);
export const getUserGoals = () => Object.values(USER_GOALS_EN);
export const getAITonePreferences = () => Object.values(AI_TONE_PREFERENCES_EN);
export const getLanguages = () => Object.values(LANGUAGES_EN);

// Type definitions for better type safety
export type ExperienceLevel = typeof EXPERIENCE_LEVELS_EN[keyof typeof EXPERIENCE_LEVELS_EN];
export type ReadingFrequency = typeof READING_FREQUENCIES_EN[keyof typeof READING_FREQUENCIES_EN];
export type DeckPreference = typeof DECK_PREFERENCES_EN[keyof typeof DECK_PREFERENCES_EN];
export type UserGoal = typeof USER_GOALS_EN[keyof typeof USER_GOALS_EN];
export type AITonePreference = typeof AI_TONE_PREFERENCES_EN[keyof typeof AI_TONE_PREFERENCES_EN];
export type Language = typeof LANGUAGES_EN[keyof typeof LANGUAGES_EN]; 